// ── the share card · one contract ────────────────────────────────────────────
//
// One image of one thing innernet noticed about a person, set over their own
// netti. Any surface that holds such a thing (a private-view fact, an inbox
// arrival, a pattern) calls `openShareCard(payload)`; the host mounted once in
// the dashboard lazily loads the studio and hands it the payload. The image is
// drawn on the device and never uploaded. Pure and client-safe.

import type { RecordRef } from '../you/record';
import type { ColorState } from '../organism/color';
import type { TunerPatch } from '../netti/tuner';
import type { FactVoice } from '../memory/voices';

export type ShareCardKind = 'fact' | 'pattern' | 'quote' | 'insight';

/** The viewer's own netti, exactly as the shell draws it (law_one_netti) —
 *  structurally the shell's `ShellNetti` (components/os/OsShellContext), kept
 *  here so this module stays pure. The seed, the CURATED colour (the still
 *  vivids it), a hand-tuned body or null, the age it wears, its ground. */
export interface ShareNetti {
  seed: number;
  color: ColorState;
  body: TunerPatch | null;
  age: number;
  ground: [number, number, number];
}

export interface ShareCardPayload {
  kind: ShareCardKind;
  /** the words on the card: a fact's statement, a pattern's reading, a quote */
  text: string;
  /** a pattern's short name, when it has one */
  title?: string | null;
  /** where it came from, in plain words ("a conversation in claude") */
  source?: string | null;
  /** ISO: when it was said (the capture's time, else the evidence's) */
  at?: string | null;
  /** how the fact rests on what was said (lib/memory/voices). The card's foot says
   *  "said <day>" only for `said`; any other voice, or none, reads "noted <day>". */
  voice?: FactVoice | null;
  /** ISO: when netti first noticed it (a fact's first version, a pattern's reading) */
  foundAt?: string | null;
  /** its disclosure class was private or self: the studio says so before it leaves */
  private?: boolean;
  /** the record it points at (for the studio's "where it lives" line) */
  record?: RecordRef | null;
  /** the person's own name, for the byline at the card's foot */
  name?: string | null;
  /** their uploaded photo, worn inside the cell beside the name (law_cell);
   *  without one the cell wears their netti's colour and their initial */
  avatarUrl?: string | null;
  /** the viewer's netti, when the caller holds it (useShareCard attaches the
   *  shell's). The studio also reads the shell and the published netti
   *  (lib/sharecard/netti), so this is optional. */
  netti?: ShareNetti | null;
}

/** the four looks a card can wear */
export type ShareStyle = 'lyric' | 'quiet' | 'letter' | 'night';
export const SHARE_STYLES: ShareStyle[] = ['lyric', 'quiet', 'letter', 'night'];

/** what a card may carry beyond the words (each can be left out) */
export interface ShareShow {
  /** where it came from */
  source: boolean;
  /** when it was said, and when netti noticed it */
  date: boolean;
  /** the byline: their cell avatar and their name */
  name: boolean;
  /** the innernet wordmark */
  mark: boolean;
}

export type ShareFormat = 'story' | 'portrait' | 'square' | 'wide';
export const SHARE_FORMAT_ORDER: ShareFormat[] = ['story', 'portrait', 'square', 'wide'];

/** the standard sizes, in export pixels */
export const SHARE_FORMATS: Record<ShareFormat, { w: number; h: number; label: string }> = {
  story: { w: 1080, h: 1920, label: 'story' },
  portrait: { w: 1080, h: 1350, label: 'portrait' },
  square: { w: 1080, h: 1080, label: 'square' },
  wide: { w: 1200, h: 630, label: 'wide' },
};

export const OPEN_SHARE_CARD_EVENT = 'innernet:share-card';

/** open the share card for this payload (the dashboard's host picks it up). */
export function openShareCard(payload: ShareCardPayload): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<ShareCardPayload>(OPEN_SHARE_CARD_EVENT, { detail: payload }));
}
